import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { QRBox } from "@/components/QRBox";
import { Loader2, X, Check, Ban, FileText, QrCode } from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
  pending: "bg-brass/10 text-brass border-brass/30",
  approved: "bg-positive-bg text-positive border-positive/30",
  rejected: "bg-brick-bg text-brick border-brick/30",
};

export default function Applications() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const applyUrl = `${window.location.origin}/apply`;

  const load = () => api.get("/applications", { params: filter ? { status: filter } : {} }).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [filter]);

  return (
    <Layout
      title="Permohonan Keanggotaan / Applications"
      subtitle="Borang digital → semakan & kelulusan Super Admin"
      actions={<button onClick={() => setShowQR(true)} data-testid="show-apply-qr-btn" className="flex items-center gap-1.5 bg-brass text-ink font-semibold hover:bg-brass-hover px-3 py-2 rounded-sm text-sm transition-colors"><QrCode size={16} /> <span className="hidden sm:inline">QR Borang</span></button>}
    >
      {!data ? (
        <div className="flex items-center gap-2 text-slate"><Loader2 className="animate-spin" size={18} />Loading…</div>
      ) : (
        <>
          <div className="flex gap-2 mb-6">
            {[["", "Semua"], ["pending", `Menunggu (${data.counts.pending})`], ["approved", `Lulus (${data.counts.approved})`], ["rejected", `Ditolak (${data.counts.rejected})`]].map(([v, l]) => (
              <button key={v} onClick={() => setFilter(v)} data-testid={`app-filter-${v || "all"}`} className={`px-3 py-1.5 rounded-sm text-sm transition-colors ${filter === v ? "bg-ink text-paper" : "bg-white border border-hairline text-slate hover:border-slate/40"}`}>{l}</button>
            ))}
          </div>

          <div className="bg-white border border-hairline rounded-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead><tr className="bg-paper border-y border-ink/20">
                  <th className="text-left text-xs uppercase tracking-wider text-slate font-medium py-3 px-4">Tarikh / Date</th>
                  <th className="text-left text-xs uppercase tracking-wider text-slate font-medium py-3 px-4">Nama / Name</th>
                  <th className="text-left text-xs uppercase tracking-wider text-slate font-medium py-3 px-4">I.C.</th>
                  <th className="text-left text-xs uppercase tracking-wider text-slate font-medium py-3 px-4">Kontak</th>
                  <th className="text-left text-xs uppercase tracking-wider text-slate font-medium py-3 px-4">Status</th>
                </tr></thead>
                <tbody>
                  {data.applications.length === 0 ? (
                    <tr><td colSpan={5} className="py-16 text-center text-slate">Tiada permohonan / No applications.</td></tr>
                  ) : data.applications.map((a) => (
                    <tr key={a.id} onClick={() => setSelected(a)} data-testid={`app-row-${a.id}`} className="ledger-row hover:bg-paper cursor-pointer transition-colors">
                      <td className="py-2.5 px-4 font-mono text-xs text-slate">{(a.submitted_at || "").slice(0, 10)}</td>
                      <td className="py-2.5 px-4 text-sm text-ink">{a.title !== "(none)" && a.title ? a.title + " " : ""}{a.name}</td>
                      <td className="py-2.5 px-4 font-mono text-sm text-slate">{a.ic_display}</td>
                      <td className="py-2.5 px-4 text-xs text-slate">{a.email || a.mobile || "—"}</td>
                      <td className="py-2.5 px-4"><span className={`inline-flex items-center border px-2 py-0.5 rounded-sm text-xs capitalize ${STATUS_BADGE[a.status]}`}>{a.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {showQR && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(18,42,67,0.4)" }} onClick={() => setShowQR(false)}>
          <div className="w-full max-w-sm animate-fade-in" onClick={(e) => e.stopPropagation()}>
            <QRBox url={applyUrl} label="QR Borang Permohonan / Membership Form" testid="apply-qrbox" />
            <p className="text-center text-paper/70 text-xs mt-3">Cetak & pamerkan QR ini — pemohon scan untuk isi borang.</p>
          </div>
        </div>
      )}

      {selected && <ReviewModal app={selected} isAdmin={user?.role === "admin"} onClose={() => setSelected(null)} onDone={() => { setSelected(null); load(); }} />}
    </Layout>
  );
}

function Row({ label, value }) {
  return <div className="flex justify-between py-2 border-b border-hairline"><span className="text-xs uppercase tracking-wider text-slate">{label}</span><span className="text-sm text-ink text-right max-w-[60%]">{value || <span className="text-slate/40">—</span>}</span></div>;
}

function ReviewModal({ app, isAdmin, onClose, onDone }) {
  const [full, setFull] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [reason, setReason] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));

  useEffect(() => { api.get(`/applications/${app.id}`).then((r) => setFull(r.data)); }, [app.id]);

  const approve = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/applications/${app.id}/approve`, { date });
      toast.success(`Diluluskan — No. Ahli ${r.data.membership_no}`);
      onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const reject = async () => {
    if (!reason.trim()) return toast.error("Sila nyatakan alasan");
    setBusy(true);
    try {
      await api.post(`/applications/${app.id}/reject`, { date, reason });
      toast.success("Permohonan ditolak");
      onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const a = full || app;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(18,42,67,0.4)" }} onClick={onClose}>
      <div className="bg-white rounded-md shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-fade-in" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-hairline sticky top-0 bg-white">
          <h3 className="font-serif text-xl text-ink flex items-center gap-2"><FileText size={18} className="text-brass" /> {a.name}</h3>
          <button onClick={onClose} className="text-slate hover:text-ink"><X size={20} /></button>
        </div>
        <div className="p-6">
          <div className="grid md:grid-cols-2 gap-x-8">
            <div>
              <Row label="Gelaran" value={a.title !== "(none)" ? a.title : null} />
              <Row label="I.C." value={a.ic_display} />
              <Row label="Jantina" value={a.gender} />
              <Row label="Status" value={a.marital} />
              <Row label="Tempat Lahir" value={a.place_of_birth} />
              <Row label="Keturunan" value={a.ethnicity} />
            </div>
            <div>
              <Row label="Emel" value={a.email} />
              <Row label="Mobil" value={a.mobile} />
              <Row label="Tel (Pej)" value={a.phone_office} />
              <Row label="Pekerjaan" value={a.occupation?.position || a.occupation?.category} />
              <Row label="Majikan" value={a.occupation?.employer_name} />
              <Row label="Syer (RM)" value={a.payment?.share_amount} />
            </div>
          </div>
          <Row label="Alamat" value={a.address} />
          <Row label="Penama" value={a.nominee?.name} />
          {a.reject_reason && <div className="mt-3 bg-brick-bg text-brick px-3 py-2 rounded-sm text-sm">Alasan tolak: {a.reject_reason}</div>}
          {a.status === "approved" && <div className="mt-3 bg-positive-bg text-positive px-3 py-2 rounded-sm text-sm">Diluluskan {a.reviewed_at} oleh {a.reviewed_by}</div>}
        </div>

        {a.status === "pending" && isAdmin && (
          <div className="px-6 py-4 border-t border-hairline sticky bottom-0 bg-white">
            {rejectMode ? (
              <div className="space-y-3">
                <input placeholder="Alasan penolakan / Reason" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reject-reason" className="w-full bg-white border border-slate-300 rounded-sm px-3 py-2 text-sm outline-none focus:border-brass text-ink" />
                <div className="flex justify-end gap-2">
                  <button onClick={() => setRejectMode(false)} className="bg-transparent border border-ink/20 text-ink px-4 py-2 rounded-sm text-sm">Batal</button>
                  <button onClick={reject} disabled={busy} data-testid="confirm-reject" className="bg-brick text-white px-4 py-2 rounded-sm text-sm flex items-center gap-2">{busy && <Loader2 size={14} className="animate-spin" />}Sahkan Tolak</button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <label className="text-xs text-slate">Tarikh Lulus:</label>
                  <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="approve-date" className="bg-white border border-slate-300 rounded-sm px-2 py-1.5 text-sm outline-none focus:border-brass text-ink" />
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setRejectMode(true)} data-testid="reject-btn" className="flex items-center gap-1.5 bg-transparent border border-brick/30 text-brick hover:bg-brick-bg px-4 py-2 rounded-sm text-sm"><Ban size={15} /> Tolak</button>
                  <button onClick={approve} disabled={busy} data-testid="approve-btn" className="flex items-center gap-1.5 bg-positive text-white hover:bg-positive/90 px-4 py-2 rounded-sm text-sm">{busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Luluskan</button>
                </div>
              </div>
            )}
          </div>
        )}
        {a.status === "pending" && !isAdmin && <div className="px-6 py-4 border-t border-hairline text-xs text-slate italic">Hanya Super Admin boleh meluluskan permohonan.</div>}
      </div>
    </div>
  );
}
